import { ref, watch } from 'vue';

/**
 * Préférences d'affichage de l'utilisateur, mémorisées dans le navigateur
 * (localStorage) et appliquées au <html> :
 *  - thème par défaut : clair / sombre / système (suit l'OS) ;
 *  - contraste élevé : mode accessibilité (classe data-contrast="high").
 *
 * On les applique AU BOOT (initPreferences, appelé dans main.ts) pour éviter tout
 * clignotement, puis les watchers persistent et ré-appliquent tout changement.
 */

export type ThemePreference = 'clair' | 'sombre' | 'systeme';

const THEME_KEY = 'bt.theme';
const CONTRAST_KEY = 'bt.contrast';

function readTheme(): ThemePreference {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_KEY) : null;
  return v === 'clair' || v === 'sombre' || v === 'systeme' ? v : 'systeme';
}
function readContrast(): boolean {
  return typeof localStorage !== 'undefined' && localStorage.getItem(CONTRAST_KEY) === 'high';
}

/** État réactif partagé (module singleton). */
export const themePreference = ref<ThemePreference>(readTheme());
export const highContrast = ref<boolean>(readContrast());

export type FontScale = 'normal' | 'confort' | 'grand';
const FONT_KEY = 'bt.fontScale';
const FONT_PX: Record<FontScale, string> = { normal: '16px', confort: '17.5px', grand: '19px' };
function readFontScale(): FontScale {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(FONT_KEY) : null;
  return v === 'confort' || v === 'grand' ? v : 'normal';
}
export const fontScale = ref<FontScale>(readFontScale());

function applyFontScale(): void {
  if (typeof document === 'undefined') return;
  // rem = base <html> : agrandir ici agrandit toute l'interface (Tailwind en rem).
  document.documentElement.style.fontSize = FONT_PX[fontScale.value];
}

function prefersDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Vrai si le thème effectif est sombre (résout « système »). */
export function isDarkResolved(): boolean {
  const t = themePreference.value;
  return t === 'sombre' || (t === 'systeme' && prefersDark());
}

function applyTheme(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', isDarkResolved());
}

function applyContrast(): void {
  if (typeof document === 'undefined') return;
  if (highContrast.value) document.documentElement.setAttribute('data-contrast', 'high');
  else document.documentElement.removeAttribute('data-contrast');
}

let mediaBound = false;

/** À appeler une fois au démarrage (avant le mount) puis à chaque session. */
export function initPreferences(): void {
  applyTheme();
  applyContrast();
  applyFontScale();
  // Le mode « système » doit suivre les changements de l'OS en direct.
  if (!mediaBound && typeof matchMedia !== 'undefined') {
    mediaBound = true;
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (themePreference.value === 'systeme') applyTheme();
    });
  }
}

export function setTheme(value: ThemePreference): void {
  themePreference.value = value;
}
export function setHighContrast(value: boolean): void {
  highContrast.value = value;
}
export function setFontScale(value: FontScale): void {
  fontScale.value = value;
}

watch(fontScale, (v) => {
  if (typeof localStorage !== 'undefined') localStorage.setItem(FONT_KEY, v);
  applyFontScale();
});

watch(themePreference, (v) => {
  if (typeof localStorage !== 'undefined') localStorage.setItem(THEME_KEY, v);
  applyTheme();
});
watch(highContrast, (v) => {
  if (typeof localStorage !== 'undefined') localStorage.setItem(CONTRAST_KEY, v ? 'high' : 'normal');
  applyContrast();
});
